import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Jaydee — the JustDeliver mascot / floating assistant.
 *
 * Sits bottom-right on every page. Gently bounces every few seconds, rotates
 * through a set of onboarding/trust messages every 25s in a speech bubble,
 * opens a full modal on click. Dismissal is remembered for 24 hours in
 * localStorage.
 *
 * Add new messages by appending to JAYDEE_MESSAGES. Each entry supports a
 * short `preview` (shown in the tiny speech bubble) and a longer `body`
 * (shown in the modal). The `body` is an array of paragraphs so we can
 * render structure without dangerouslySetInnerHTML.
 */

const JAYDEE_IMG =
  'https://customer-assets-eiarnc6j.emergentagent.net/job_driver-marketplace-16/artifacts/rec8lsmq_55479.png';

const STORAGE_HIDE_UNTIL = 'jd_jaydee_hide_until';
const STORAGE_LAST_MSG   = 'jd_jaydee_last_msg_idx';
const STORAGE_POSITION   = 'jd_jaydee_pos';         // {x,y} offset in px from default corner
const HIDE_MS            = 24 * 60 * 60 * 1000;  // 24 hours
const ROTATE_MS          = 25_000;
const DRAG_THRESHOLD_PX  = 4;                    // movement below this counts as a click, not a drag

const JAYDEE_MESSAGES = [
  {
    id: 'payment_protection',
    title: '👋 Hi, I\'m Jaydee!',
    preview: '👋 Hi, I\'m Jaydee — your friendly Payment Guardian.',
    body: [
      'Your friendly Payment Guardian. 🛡️📦',
      '🔒 Your payment is secure with JustDeliver.',
      'You\'ll pay JustDeliver\'s charges securely by card, while the remaining delivery amount is paid directly to your driver.',
      'Every card payment is processed securely through Stripe, and every driver on JustDeliver is verified before they can bid.',
      'If anything goes wrong before your delivery, our cancellation refund protection has you covered — and my team is always here to help.',
    ],
  },
  {
    id: 'post_a_delivery',
    title: '👀 Go on… Post a Delivery!',
    preview: '👀 Got something that needs moving? Post a delivery!',
    body: [
      'I\'ve got a funny feeling you\'ve got something that needs moving… 😏',
      'Whether it\'s a sofa, a Facebook Marketplace bargain, a bed, a table, a wardrobe, or something completely random — post your delivery and let local drivers compete with their best prices.',
      'Compare bids, choose your favourite driver, and leave the rest to us.',
      'Go on… ya know ya want to! 📦🚚',
    ],
  },
  {
    id: 'youre_in_control',
    title: '😎 You\'re in Control!',
    preview: '😎 Compare bids, pick your driver, and book with confidence.',
    body: [
      'I\'m all about giving you the choice.',
      'When you post a delivery on JustDeliver, local drivers send you their best prices. Compare bids, check driver ratings, and choose the driver that\'s right for you.',
      'No chasing quotes. No endless phone calls. Just post your delivery, compare your options, and book with confidence.',
      'Simple, fair, and all in your control. 🚚📦',
    ],
  },
];

function readInitialIndex() {
  try {
    const raw = localStorage.getItem(STORAGE_LAST_MSG);
    const n = raw == null ? 0 : parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 && n < JAYDEE_MESSAGES.length ? n : 0;
  } catch { return 0; }
}

function isHidden() {
  try {
    const raw = localStorage.getItem(STORAGE_HIDE_UNTIL);
    const t = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(t) && t > Date.now();
  } catch { return false; }
}

export default function JaydeeMascot() {
  const [hidden, setHidden]   = useState(() => isHidden());
  const [idx, setIdx]         = useState(readInitialIndex);
  const [open, setOpen]       = useState(false);
  const [showBubble, setShowBubble] = useState(true);
  // Drag-to-move — Jaydee sits in the bottom-right corner by default. The
  // customer can drag him anywhere on screen; the offset from that
  // default anchor is persisted so his position sticks across page loads.
  //   • offset.x  = pixels shifted LEFT  from the default right anchor
  //   • offset.y  = pixels shifted UP    from the default bottom anchor
  // The offset is clamped on drop so he can't be flung off-screen.
  const [offset, setOffset] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_POSITION);
      if (raw) {
        const p = JSON.parse(raw);
        if (Number.isFinite(p?.x) && Number.isFinite(p?.y)) return p;
      }
    } catch { /* ignore parse errors */ }
    return { x: 0, y: 0 };
  });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0, moved: false });

  // Rotate messages every ROTATE_MS. Skip while the modal is open so the
  // user can read at their own pace.
  useEffect(() => {
    if (hidden || open) return;
    const id = setInterval(() => {
      setIdx((prev) => {
        const next = (prev + 1) % JAYDEE_MESSAGES.length;
        try { localStorage.setItem(STORAGE_LAST_MSG, String(next)); } catch { /* localStorage may be blocked */ }
        return next;
      });
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [hidden, open]);

  const persistIdx = useCallback((next) => {
    setIdx(next);
    try { localStorage.setItem(STORAGE_LAST_MSG, String(next)); } catch { /* localStorage may be blocked */ }
  }, []);

  const handleDismiss = useCallback(() => {
    try { localStorage.setItem(STORAGE_HIDE_UNTIL, String(Date.now() + HIDE_MS)); } catch { /* localStorage may be blocked */ }
    setHidden(true);
    setOpen(false);
  }, []);

  const goPrev = useCallback(() => {
    persistIdx((idx - 1 + JAYDEE_MESSAGES.length) % JAYDEE_MESSAGES.length);
  }, [idx, persistIdx]);
  const goNext = useCallback(() => {
    persistIdx((idx + 1) % JAYDEE_MESSAGES.length);
  }, [idx, persistIdx]);

  // Drag handlers — use Pointer Events so a single implementation covers
  // mouse + touch + stylus. We update `offset` via requestAnimationFrame
  // during the drag and persist on release so localStorage isn't touched
  // on every pixel of movement.
  const onPointerDown = useCallback((e) => {
    // Only left mouse / primary touch. Right-click / middle-click ignored.
    if (e.button !== undefined && e.button !== 0) return;
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      baseX: offset.x,
      baseY: offset.y,
      moved: false,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not all browsers support pointer capture */ }
  }, [offset.x, offset.y]);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) return;
      d.moved = true;
      setDragging(true);
      setShowBubble(false); // hide bubble while dragging so it doesn't fly off with him
    }
    // Anchor is bottom-right, so a positive right-anchor offset (`x`) moves
    // him LEFT (dx is negative when dragging left → increases x). Same for y.
    setOffset({ x: d.baseX - dx, y: d.baseY - dy });
  }, []);

  const clampOffset = useCallback((pos) => {
    if (typeof window === 'undefined') return pos;
    // Keep the mascot on-screen. His footprint is ~80x80, default anchor
    // uses right-4/bottom-4 → allow him from that corner up to almost the
    // opposite edge.
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxX = Math.max(0, vw - 96);   // leave 96px so he doesn't hit the left edge
    const maxY = Math.max(0, vh - 96);
    return {
      x: Math.min(Math.max(0, pos.x), maxX),
      y: Math.min(Math.max(0, pos.y), maxY),
    };
  }, []);

  const onPointerUp = useCallback((e) => {
    const d = dragRef.current;
    if (!d.active) return;
    dragRef.current = { ...d, active: false };
    if (!d.moved) {
      setDragging(false);
      return;
    }
    setOffset((prev) => {
      const clamped = clampOffset(prev);
      try { localStorage.setItem(STORAGE_POSITION, JSON.stringify(clamped)); } catch { /* localStorage may be blocked */ }
      return clamped;
    });
    // Swallow the trailing click that fires after a drag ends — otherwise
    // the modal would open every time the user releases. Capture the
    // element locally because React nulls `e.currentTarget` after this
    // handler returns.
    const el = e.currentTarget;
    if (el) {
      const swallow = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        el.removeEventListener('click', swallow, true);
      };
      el.addEventListener('click', swallow, true);
    }
    setTimeout(() => setDragging(false), 0);
  }, [clampOffset]);

  // If the viewport resizes (rotate/pinch/zoom) clamp him back on-screen.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setOffset((prev) => clampOffset(prev));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clampOffset]);

  if (hidden) return null;

  const msg = JAYDEE_MESSAGES[idx];

  return (
    <>
      {/* Local styles for the mascot's float animation + bubble arrow.
          Keeps the component self-contained so it can be dropped anywhere. */}
      <style>{`
        @keyframes jaydee-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        .jaydee-float { animation: jaydee-float 3.6s ease-in-out infinite; }
        @keyframes jaydee-bubble-in {
          0%   { opacity: 0; transform: translateY(6px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0)   scale(1);    }
        }
        .jaydee-bubble-in { animation: jaydee-bubble-in 0.35s ease-out both; }
      `}</style>

      {/* Floating mascot + speech bubble anchor. Uses pointer-events-none on
          the outer wrapper so it never blocks clicks on underlying UI
          outside its own hitboxes. On ≥sm we shift Jaydee LEFT of the
          WhatsApp FAB (which owns right-6 / bottom-20) so they sit
          side-by-side instead of overlapping. */}
      <div
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-24 z-40 pointer-events-none flex items-end gap-2 max-w-[min(320px,calc(100vw-2rem))]"
        style={{ transform: `translate(${-offset.x}px, ${-offset.y}px)` }}
        data-testid="jaydee-container"
      >
        {/* Speech bubble */}
        {showBubble && !open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="jaydee-bubble-in pointer-events-auto relative bg-gradient-to-br from-[#16a34a] to-[#22c55e] text-white text-sm font-medium shadow-xl rounded-2xl rounded-br-none px-3.5 py-2.5 leading-snug max-w-[220px] sm:max-w-[240px] text-left hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 transition"
            key={msg.id}  // remounts on rotate → replays the fade-in animation
            data-testid="jaydee-bubble"
            aria-label={`Jaydee says: ${msg.preview}`}
          >
            {/* Close-bubble X — hides the bubble but keeps Jaydee bouncing */}
            <span
              onClick={(e) => { e.stopPropagation(); setShowBubble(false); }}
              className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-white text-gray-600 rounded-full flex items-center justify-center text-[10px] shadow border border-gray-200 hover:bg-gray-100"
              aria-hidden="true"
              data-testid="jaydee-bubble-close"
            >
              ×
            </span>
            <span className="block">{msg.preview}</span>
            <span className="block text-[10px] mt-1 text-white/80">Tap to read more</span>
            {/* Speech-bubble arrow pointing down-right towards Jaydee */}
            <span
              aria-hidden="true"
              className="absolute -bottom-1.5 right-3 w-3 h-3 rotate-45 bg-gradient-to-br from-[#16a34a] to-[#22c55e]"
            />
          </button>
        )}

        {/* Mascot — draggable with pointer events. A click without
            movement still opens the modal; a genuine drag ≥DRAG_THRESHOLD
            moves him instead (and the trailing click is swallowed). */}
        <button
          type="button"
          onClick={() => { if (!dragging) setOpen(true); }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{ touchAction: 'none', cursor: dragging ? 'grabbing' : 'grab' }}
          className={`${dragging ? '' : 'jaydee-float'} pointer-events-auto relative rounded-full focus:outline-none focus:ring-4 focus:ring-emerald-400/60 select-none`}
          aria-label="Open Jaydee's message (drag to move)"
          data-testid="jaydee-mascot"
        >
          <img
            src={JAYDEE_IMG}
            alt="Jaydee, JustDeliver's friendly delivery mascot, waving hello"
            className="w-16 h-16 sm:w-20 sm:h-20 object-contain drop-shadow-xl select-none pointer-events-none"
            draggable="false"
          />
        </button>
      </div>

      {/* Full modal — only mounts when open to keep the DOM light */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="jaydee-modal-backdrop"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="jaydee-modal-title"
            className="jaydee-bubble-in relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="jaydee-modal"
          >
            {/* Green banner with mascot avatar */}
            <div className="relative bg-gradient-to-br from-[#16a34a] to-[#22c55e] px-5 pt-5 pb-4 text-white">
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close Jaydee"
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
                data-testid="jaydee-close-btn"
              >
                <X className="w-4 h-4 text-white" />
              </button>
              <div className="flex items-center gap-3 pr-8">
                <img
                  src={JAYDEE_IMG}
                  alt=""
                  className="w-14 h-14 sm:w-16 sm:h-16 object-contain drop-shadow-md select-none flex-shrink-0"
                  draggable="false"
                />
                <div className="min-w-0">
                  <h2 id="jaydee-modal-title" className="text-lg sm:text-xl font-bold font-['Inter'] tracking-tight">
                    {msg.title}
                  </h2>
                  <p className="text-[11px] uppercase tracking-wider text-white/80 mt-0.5">
                    Message {idx + 1} of {JAYDEE_MESSAGES.length}
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-5 py-4 max-h-[60vh] overflow-y-auto space-y-2.5 text-sm text-gray-800 leading-relaxed" data-testid="jaydee-body">
              {msg.body.map((p, i) => (
                <p key={i} className="whitespace-pre-wrap">{p}</p>
              ))}
            </div>

            {/* Footer — Prev / Next / Don't show */}
            <div className="px-5 pb-4 pt-2 flex items-center justify-between gap-2 border-t border-gray-100 bg-gray-50/60">
              <button
                type="button"
                onClick={goPrev}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
                data-testid="jaydee-prev-btn"
                aria-label="Previous Jaydee message"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2"
                data-testid="jaydee-hide-24h-btn"
              >
                Don&apos;t show for 24 hours
              </button>
              <button
                type="button"
                onClick={goNext}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold text-white bg-[#2E7D32] rounded-md hover:bg-[#1B5E20] focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="jaydee-next-btn"
                aria-label="Next Jaydee message"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
